/**
 * Список услуг категории — /services/{categoryCode} (canon design-guest §15).
 *
 * Tile-rows «иконка слева + название + chevron ›» (52px), H1 «Что Вам
 * нужно?» + lead. Тап на услугу → /requests/new?service={itemId};
 * «Другое — опишите» — ПОСЛЕДНЯЯ tile-строка списка (не кнопка под ним) →
 * /requests/new?category={code}&service=other.
 *
 * Состояния (canon §15):
 *   - loading — 5 tile-row skeleton'ов (не грид карточек);
 *   - empty — «В этой категории пока ничего нет…» + кнопка «Другое»;
 *   - error — toast retry + fallback-кнопка «Другое».
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, CategoryIcon, Skeleton } from '@hsc/ui';
import { displayItemPrice, isExternalLinkItem, type CatalogViewItem } from '@hsc/types';
import { resolveGuestLocale } from '../../lib/locale.js';
import { Toast } from '../../shared/ui/Toast.js';
import { useServiceCatalog } from './useCatalog.js';

/**
 * Открыть ссылку вендора в новой вкладке. noopener/noreferrer — чтобы
 * чужая страница не получила доступ к window.opener (безопасность).
 */
function openExternal(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export interface CategoryServicesPageProps {
  /**
   * ВРЕМЕННО: компактные цены справа в tile-строке (решение Артёма по
   * ценам в списке ожидается). Убрать цены из списка = передать false
   * (или поменять default) — одна правка, вёрстка не меняется.
   */
  showPricesInList?: boolean;
}

/** Tile-строка услуги: иконка слева, название, (цена), chevron справа. */
function ServiceTile({
  item,
  price,
  onOpen,
}: {
  item: CatalogViewItem;
  price: string | null;
  onOpen: () => void;
}): JSX.Element {
  return (
    <li>
      <button
        type="button"
        className="cat-tile"
        data-testid={`catalog-item-${item.code}`}
        onClick={onOpen}
      >
        <span className="cat-tile__icon">
          <CategoryIcon code={item.code} size={18} />
        </span>
        <span className="cat-tile__label">{item.name}</span>
        {price !== null && <span className="cat-tile__price">{price}</span>}
        <span className="cat-tile__chev" aria-hidden="true">
          ›
        </span>
      </button>
    </li>
  );
}

/**
 * Карточка услуги-внешней-ссылки («Облака» #31): трансфер / экскурсии.
 * Заявку НЕ создаёт — кнопка «Открыть» открывает сайт вендора в новой
 * вкладке (window.open). Иконка ↗ и подсказка отличают её от обычных услуг.
 */
function ExternalLinkTile({
  item,
  url,
}: {
  item: CatalogViewItem;
  url: string;
}): JSX.Element {
  return (
    <li>
      <div className="cat-tile cat-tile--external" data-testid={`catalog-item-${item.code}`}>
        <span className="cat-tile__icon">
          <CategoryIcon code={item.code} size={18} />
        </span>
        <span className="cat-tile__body">
          <span className="cat-tile__label">{item.name}</span>
          {item.description !== undefined && item.description !== '' && (
            <span className="cat-tile__desc">{item.description}</span>
          )}
        </span>
        <Button
          variant="secondary"
          className="cat-tile__open"
          data-testid={`catalog-item-${item.code}-open`}
          aria-label={`Открыть «${item.name}» на сайте партнёра в новой вкладке`}
          onClick={() => openExternal(url)}
        >
          Открыть ↗
        </Button>
      </div>
    </li>
  );
}

export function CategoryServicesPage({
  showPricesInList = true,
}: CategoryServicesPageProps): JSX.Element {
  const { categoryCode = '' } = useParams<{ categoryCode: string }>();
  const navigate = useNavigate();
  const locale = useMemo(() => resolveGuestLocale(), []);
  const { data, isLoading, isError, refetch } = useServiceCatalog(locale);
  const [toastHidden, setToastHidden] = useState(false);

  const otherPath = `/requests/new?category=${encodeURIComponent(categoryCode)}&service=other`;
  const goOther = (): void => void navigate(otherPath);
  const goBack = (): void => void navigate('/services');

  if (isLoading) {
    return (
      <section aria-busy="true" data-testid="category-skeleton">
        <button type="button" className="ta-back" onClick={goBack}>
          ‹ Услуги отеля
        </button>
        <h1 className="hsc-page__title">Что Вам нужно?</h1>
        <ul className="cat-tiles">
          {Array.from({ length: 5 }, (_, i) => (
            <li key={i} className="cat-tile cat-tile--skeleton" data-testid="tile-skeleton">
              <Skeleton width={32} height={32} radius={10} />
              <Skeleton height={14} radius={6} />
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section data-testid="category-error">
        {!toastHidden && (
          <Toast
            tone="error"
            message="Не удалось загрузить услуги категории."
            actionLabel="Повторить"
            onAction={() => {
              setToastHidden(false);
              void refetch();
            }}
            onClose={() => setToastHidden(true)}
          />
        )}
        <button type="button" className="ta-back" onClick={goBack}>
          ‹ Услуги отеля
        </button>
        <h1 className="hsc-page__title">Что Вам нужно?</h1>
        <Card>
          <p>Список услуг сейчас недоступен. Попросите свободным текстом — мы поможем.</p>
          <Button variant="secondary" onClick={goOther}>
            Другое — опишите
          </Button>
        </Card>
      </section>
    );
  }

  const category = data.categories.find((c) => c.code === categoryCode);

  if (!category) {
    return (
      <section>
        <button type="button" className="ta-back" onClick={goBack}>
          ‹ Услуги отеля
        </button>
        <h1 className="hsc-page__title">Что Вам нужно?</h1>
        <Card>
          <p role="alert">Такой категории больше нет — посмотрите весь каталог услуг.</p>
          <Button variant="secondary" onClick={goBack}>
            К каталогу услуг
          </Button>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid={`catalog-items-${category.code}`}>
      <button type="button" className="ta-back" onClick={goBack}>
        ‹ Услуги отеля
      </button>
      <h1 className="hsc-page__title">Что Вам нужно?</h1>
      <p className="ta-muted">Выберите услугу — заполните детали — мы быстро принесём.</p>

      {category.items.length === 0 ? (
        <Card data-testid="category-empty">
          <p>В этой категории пока ничего нет. Попросите свободным текстом —</p>
          <Button variant="secondary" onClick={goOther}>
            Другое
          </Button>
        </Card>
      ) : (
        <ul className="cat-tiles">
          {category.items.map((item) =>
            isExternalLinkItem(item) ? (
              <ExternalLinkTile key={item.id} item={item} url={item.external_url} />
            ) : (
              <ServiceTile
                key={item.id}
                item={item}
                price={showPricesInList ? displayItemPrice(item, locale) : null}
                onOpen={() =>
                  navigate(`/requests/new?service=${encodeURIComponent(item.id)}`)
                }
              />
            ),
          )}
          {/* «Другое — опишите» — последняя tile-строка (canon §15) */}
          <li>
            <button
              type="button"
              className="cat-tile"
              data-testid="catalog-item-other"
              onClick={goOther}
            >
              <span className="cat-tile__icon">
                <CategoryIcon code="other" size={18} />
              </span>
              <span className="cat-tile__label">Другое — опишите</span>
              <span className="cat-tile__chev" aria-hidden="true">
                ›
              </span>
            </button>
          </li>
        </ul>
      )}
    </section>
  );
}
